import { useState } from 'react';
import {
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { PuzzleItem } from 'src/api/types';
import { useCreatePuzzleItemMutation } from 'src/api/puzzle-item/createPuzzleItem';
import { useUpdatePuzzleItemMutation } from 'src/api/puzzle-item/updatePuzzleItem';
import { CategoryIdFormSelect } from 'src/components/form/CategoryIdFormSelect';
import { LanguageCodeFormSelect } from 'src/components/form/LanguageCodeFormSelect';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';

interface Props {
  open: boolean;
  onClose: () => void;
  item?: PuzzleItem;
}

export default function PuzzleItemDialog({ onClose, ...props }: Props) {
  const { languageCode } = useGlobalFiltersStore();

  const defaultState = {
    name: '',
    categoryId: 0,
    languageCode,
  } as unknown as PuzzleItem;

  const [item, setItem] = useState<PuzzleItem>(props.item ?? defaultState);

  const { mutateAsync: createPuzzleItemMutation, isPending: isCreatePending } =
    useCreatePuzzleItemMutation();
  const { mutateAsync: updatePuzzleItemMutation, isPending: isUpdatePending } =
    useUpdatePuzzleItemMutation();

  const savePuzzleItem = async (item: PuzzleItem) => {
    item.name = item.name.trim();

    if (item.id) {
      await updatePuzzleItemMutation(item);
      return;
    }
    await createPuzzleItemMutation(item);
  };

  const handleNameChange = (e: any) => {
    setItem((prevState) => ({
      ...prevState,
      name: e.target.value,
    }));
  };

  const handleCategoryChange = (_e: any, value: number) => {
    setItem((prevState) => ({
      ...prevState,
      categoryId: value,
    }));
  };

  const onSuccess = async () => {
    await savePuzzleItem(item);
    setItem(defaultState);
    onClose();
  };

  const onCancel = () => {
    setItem(defaultState);
    onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>
        {item?.id ? `Edit ${item?.id}` : 'Create'} Puzzle Item
      </DialogTitle>
      <DialogContent sx={{ width: '600px' }}>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id='name'
                label='Name'
                value={item.name}
                onChange={handleNameChange}
                sx={{ width: '100%' }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <LanguageCodeFormSelect
                label='Sport'
                defaultValue={languageCode}
                value={item.languageCode}
                onChange={(e) => {
                  setItem({ ...item, languageCode: e.target.value });
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <CategoryIdFormSelect
                value={item.categoryId}
                onChange={handleCategoryChange}
                sport={item.languageCode}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={onSuccess}
          autoFocus
          disabled={isCreatePending || isUpdatePending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
